// @ts-strict-ignore
import { Log } from '@shared/debug/log';
import { escape } from 'lodash';
import { TranslationParams } from '@shared/manager/locale-resource.manager';
import { NumberUtils } from './number-utils';

export class HtmlUtils {

  /** Escapes strings in translationParams so they can't inject HTML/JS into a translated message that gets rendered via [innerHTML]. */
  public static sanitizeTranslationParams(params: TranslationParams | undefined): TranslationParams | undefined {
    if (!params) {
      return params;
    }
    if (Array.isArray(params)) {
      return params.map((value) => typeof value === 'string' ? escape(value) : value);
    }
    const sanitized: TranslationParams = {};
    for (const key of Object.keys(params)) {
      const value = params[key];
      sanitized[key] = typeof value === 'string' ? escape(value) : value;
    }
    return sanitized;
  }

  public static copyTextToClipboard(link: string): void {
    let range: Range, selection: Selection, mark: HTMLSpanElement;
    try {
      range = document.createRange();
      selection = document.getSelection();
      selection.removeAllRanges();

      mark = document.createElement('span');
      mark.textContent = link;
      // reset user styles for span element
      mark.style.all = 'unset';
      // prevents scrolling to the end of the page
      mark.style.position = 'fixed';
      mark.style.top = "0px";
      mark.style.clip = 'rect(0, 0, 0, 0)';
      // used to preserve spaces and line breaks
      mark.style.whiteSpace = 'pre';
      // do not inherit user-select (it may be `none`)
      mark.style.webkitUserSelect = 'text';
      mark.style.userSelect = 'text';
      mark.style.userSelect = 'text';

      document.body.appendChild(mark);

      range.selectNode(mark);
      selection.addRange(range);

      const successful: boolean = document.execCommand('copy');
      if (!successful) {
        throw new Error('copy command was unsuccessful');
      }
    } catch (err) {
      Log.info(err);
    } finally {
      if (selection) {
        if (typeof selection.removeRange === 'function') {
          selection.removeRange(range);
        } else {
          selection.removeAllRanges();
        }
      }

      if (mark) {
        document.body.removeChild(mark);
      }
    }
  }

  public static createLegacyFontParagraph(text: string, options: {
    align?: string,
    fontFace?: string,
    fontSize?: string | number,
    color?: string,
    bold?: boolean,
    italic?: boolean,
    underline?: boolean,
  } = {}): string {
    const paragraphTag: string = options.align === undefined ? '<P>' : `<P ALIGN="${options.align}">`;
    const faceAttribute: string = options.fontFace === undefined ? '' : ` FACE="${options.fontFace}"`;
    const fontSize: string = options.fontSize === undefined ? '' : `${options.fontSize}`;
    const color: string = options.color ?? '#000000';
    let decoratedText: string = text ?? '';

    if (options.bold) {
      decoratedText = `<B>${decoratedText}</B>`;
    }
    if (options.italic) {
      decoratedText = `<I>${decoratedText}</I>`;
    }
    if (options.underline) {
      decoratedText = `<U>${decoratedText}</U>`;
    }

    return `${paragraphTag}<FONT${faceAttribute} SIZE="${fontSize}" COLOR="${color}">${decoratedText}</FONT></P>`;
  }

  public static updateLegacyFontParagraphFontSize(html: string, options: { fontSize: string | number }): string {
    if (html == null) {
      return html;
    }

    return html.replaceAll(/(<FONT\b[^>]*\bSIZE=")([^"]*)(")/ig, `$1${options.fontSize}$3`);
  }

  public static extractLegacyFontSizeValues(html: string): number[] {
    if (html == null) {
      return [];
    }

    const sizes: string[] = [];
    const fontSizeRegex: RegExp = /<FONT\b[^>]*\bSIZE\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/ig;
    let match: RegExpExecArray;

    while ((match = fontSizeRegex.exec(html)) !== null) {
      sizes.push((match[1] ?? match[2] ?? match[3] ?? '').trim());
    }

    return sizes.map(Number).filter((size) => NumberUtils.isNumber(size));
  }

}
